// ============================================================================
// Zod Валидаторы для Frontend
// Фронтенд валидация данных перед отправкой в Cloud Functions
// ============================================================================

import { z } from 'zod';

// ============================================================================
// СХЕМЫ ВАЛИДАЦИИ ДЛЯ ПРОЕКТОВ
// ============================================================================

/**
 * Схема для создания проекта
 */
export const createProjectSchema = z.object({
  name: z.string().min(1, 'Назва обов\'язкова').max(100, 'Назва занадто довга'),
  description: z.string().max(500, 'Опис занадто довгий').optional(),
  isDemo: z.boolean().optional(),
});

export type CreateProjectInput = z.infer<typeof createProjectSchema>;

/**
 * Схема для обновления проекта
 */
export const updateProjectSchema = z.object({
  appId: z.string().min(1, 'ID проекту обов\'язковий'),
  name: z.string().min(1).max(100).optional(),
  description: z.string().max(500).optional(),
  icon: z.string().optional(),
  status: z.enum(['active', 'archived']).optional(),
});

export type UpdateProjectInput = z.infer<typeof updateProjectSchema>;

/**
 * Схема для удаления проекта
 */
export const deleteProjectSchema = z.object({
  appId: z.string().min(1, 'ID проекту обов\'язковий'),
});

export type DeleteProjectInput = z.infer<typeof deleteProjectSchema>;

// ============================================================================
// СХЕМЫ ВАЛИДАЦИИ ДЛЯ СОБЫТИЙ
// ============================================================================

/**
 * Схема для создания события
 */
export const addEventSchema = z.object({
  appId: z.string().min(1, 'ID проекту обов\'язковий'),
  versionId: z.string().min(1, 'ID версії обов\'язковий'),
  screenId: z.string().min(1, 'ID екрану обов\'язковий'),
  eventId: z.string().min(1, 'ID події обов\'язковий'),
  name: z.string().min(1, 'Назва події обов\'язкова').max(100, 'Назва занадто довга'),
  type: z.enum(['click', 'view', 'error', 'custom', 'input', 'success', 'swipe', 'scroll']),
  description: z.string().max(500).optional(),
  x: z.number().min(0).optional(),
  y: z.number().min(0).optional(),
  params: z.array(z.object({
    key: z.string().min(1),
    value: z.string(),
    type: z.enum(['string', 'number', 'boolean']).optional(),
  })).optional(),
});

export type AddEventInput = z.infer<typeof addEventSchema>;

/**
 * Схема для обновления события
 */
export const updateEventSchema = z.object({
  appId: z.string().min(1, 'ID проекту обов\'язковий'),
  versionId: z.string().min(1, 'ID версії обов\'язковий'),
  screenId: z.string().min(1, 'ID екрану обов\'язковий'),
  eventId: z.string().min(1, 'ID події обов\'язковий'),
  name: z.string().min(1).max(100).optional(),
  type: z.enum(['click', 'view', 'error', 'custom', 'input', 'success', 'swipe', 'scroll']).optional(),
  description: z.string().max(500).optional(),
  x: z.number().min(0).optional(),
  y: z.number().min(0).optional(),
  status: z.enum(['new', 'edited', 'implemented', 'pending_deletion', 'deleted']).optional(),
  params: z.array(z.object({
    key: z.string().min(1),
    value: z.string(),
    type: z.enum(['string', 'number', 'boolean']).optional(),
  })).optional(),
  implemented: z.boolean().optional(),
  // Добавлено для поддержки комментариев и статуса
  comments: z.array(z.object({
    id: z.string(),
    text: z.string(),
    author: z.string(),
    authorId: z.string(),
    authorRole: z.string().optional(),
    projectRole: z.string().optional(),
    createdAt: z.string(),
  })).optional(),
  lastStatusChangedBy: z.enum(['analyst', 'developer', 'viewer']).optional(),
  previousStatus: z.string().optional(),
  previousValues: z.record(z.string(), z.unknown()).optional(),
});

export type UpdateEventInput = z.infer<typeof updateEventSchema>;

/**
 * Схема для удаления события
 */
export const deleteEventSchema = z.object({
  appId: z.string().min(1, 'ID проекту обов\'язковий'),
  versionId: z.string().min(1, 'ID версії обов\'язковий'),
  screenId: z.string().min(1, 'ID екрану обов\'язковий'),
  eventId: z.string().min(1, 'ID події обов\'язковий'),
});

export type DeleteEventInput = z.infer<typeof deleteEventSchema>;

// ============================================================================
// СХЕМЫ ВАЛИДАЦИИ ДЛЯ ВЕРСИЙ
// ============================================================================

/**
 * Схема для создания версии
 */
export const addVersionSchema = z.object({
  appId: z.string().min(1, 'ID проекту обов\'язковий'),
  version: z.string().min(1, 'Номер версії обов\'язковий').max(20, 'Номер занадто довгий'),
  name: z.string().max(100).optional(),
  copyScreens: z.boolean().optional(),
});

export type AddVersionInput = z.infer<typeof addVersionSchema>;

/**
 * Схема для обновления версии
 */
export const updateVersionSchema = z.object({
  appId: z.string().min(1, 'ID проекту обов\'язковий'),
  versionId: z.string().min(1, 'ID версії обов\'язковий'),
  version: z.string().min(1).max(20).optional(),
  name: z.string().min(1).max(100).optional(),
  isActive: z.boolean().optional(),
});

export type UpdateVersionInput = z.infer<typeof updateVersionSchema>;

/**
 * Схема для удаления версии
 */
export const deleteVersionSchema = z.object({
  appId: z.string().min(1, 'ID проекту обов\'язковий'),
  versionId: z.string().min(1, 'ID версії обов\'язковий'),
});

export type DeleteVersionInput = z.infer<typeof deleteVersionSchema>;

// ============================================================================
// СХЕМЫ ВАЛИДАЦИИ ДЛЯ ЭКРАНОВ
// ============================================================================

/**
 * Схема для создания экрана
 */
export const addScreenSchema = z.object({
  appId: z.string().min(1, 'ID проекту обов\'язковий'),
  versionId: z.string().min(1, 'ID версії обов\'язковий'),
  name: z.string().min(1, 'Ім\'я екрану обов\'язкове').max(100, 'Ім\'я занадто довге'),
  displayName: z.string().min(1, 'Назва екрану обов\'язкова').max(100, 'Назва занадто довга'),
  imageUrl: z.string().min(0).optional().nullable(),
});

export type AddScreenInput = z.infer<typeof addScreenSchema>;

/**
 * Схема для обновления экрана
 */
export const updateScreenSchema = z.object({
  appId: z.string().min(1, 'ID проекту обов\'язковий'),
  versionId: z.string().min(1, 'ID версії обов\'язковий'),
  screenId: z.string().min(1, 'ID екрану обов\'язковий'),
  name: z.string().min(1).max(100).nullish(),
  displayName: z.string().min(1).max(100).nullish(),
  imageUrl: z.string().url('Невірний URL').nullish(),
  markerScale: z.number().min(0.1).max(5).nullish(),
  popupScale: z.number().min(0.1).max(5).nullish(),
});

export type UpdateScreenInput = z.infer<typeof updateScreenSchema>;

/**
 * Схема для удаления экрана
 */
export const deleteScreenSchema = z.object({
  appId: z.string().min(1, 'ID проекту обов\'язковий'),
  versionId: z.string().min(1, 'ID версії обов\'язковий'),
  screenId: z.string().min(1, 'ID екрану обов\'язковий'),
});

export type DeleteScreenInput = z.infer<typeof deleteScreenSchema>;

// ============================================================================
// СХЕМЫ ВАЛИДАЦИИ ДЛЯ УЧАСТНИКОВ
// ============================================================================

/**
 * Схема для приглашения пользователя
 */
export const inviteUserSchema = z.object({
  appId: z.string().min(1, 'ID проекту обов\'язковий'),
  email: z.string().email('Невірний формат email'),
  role: z.enum(['analyst', 'developer', 'viewer']),
});

export type InviteUserInput = z.infer<typeof inviteUserSchema>;

/**
 * Схема для удаления участника
 */
export const removeMemberSchema = z.object({
  appId: z.string().min(1, 'ID проекту обов\'язковий'),
  memberId: z.string().min(1, 'ID учасника обов\'язковий'),
});

export type RemoveMemberInput = z.infer<typeof removeMemberSchema>;

/**
 * Схема для обновления роли участника
 */
export const updateMemberRoleSchema = z.object({
  appId: z.string().min(1, 'ID проекту обов\'язковий'),
  memberId: z.string().min(1, 'ID учасника обов\'язковий'),
  role: z.enum(['analyst', 'developer', 'viewer']),
});

export type UpdateMemberRoleInput = z.infer<typeof updateMemberRoleSchema>;

// ============================================================================
// УТИЛИТЫ ДЛЯ ВАЛИДАЦИИ
// ============================================================================

/**
 * Тип результата валидации
 */
export interface ValidationResult<T> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * Универсальная функция валидации
 */
export function validate<T>(schema: z.ZodSchema<T>, data: unknown): ValidationResult<T> {
  try {
    const validated = schema.parse(data);
    return { success: true, data: validated };
  } catch (error) {
    if (error instanceof z.ZodError) {
      const message = error.issues.map((e) => e.message).join(', ');
      return { success: false, error: message };
    }
    return { success: false, error: 'Помилка валідації' };
  }
}

/**
 * Безопасная валидация - не выбрасывает исключение
 */
export function safeValidate<T>(schema: z.ZodSchema<T>, data: unknown): T | null {
  try {
    return schema.parse(data);
  } catch {
    return null;
  }
}
